import type { AwardSummary } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, RefObject } from 'react';
import { Link } from 'react-aria-components';
import { myAwardsQuery } from '../api/awards';
import { forWhomWords, statusOf, summaryAmount } from '../awards/present';
import { Card, DataTable, StatusPill, formatShortDate, plural, type DataColumn } from '../ui';
import type { SectorPanelModel } from './model';
import s from './Home.module.css';

/** How many entries Home lists; the rest are on My drafts & submitted. */
const SHOWN = 8;

/**
 * D1 · My entries: the awards a delegate entered for their Sector Lead in this sector, each with
 * "by … for …" and where it is now (Q16 default: delegates see their own entries).
 */
export function MyEntriesCard({
  panel,
  myName,
  headingRef,
}: {
  panel: SectorPanelModel;
  myName: string;
  /** The hero's "My entries" moves focus here (D1). */
  headingRef?: RefObject<HTMLHeadingElement | null> | undefined;
}): ReactElement {
  const mine = useQuery(myAwardsQuery);
  const lead = panel.forPerson?.name ?? 'the Sector Lead';
  // D1.empty calls the Sector Lead by first name, as "Start an award for Graham" does.
  const leadFirst =
    panel.forPerson === null ? lead : (panel.forPerson.name.split(' ')[0] ?? panel.forPerson.name);
  // This year's drafts and the newest page of what was sent (QA-014: one year, 25 at a time).
  const entries = (
    mine.data === undefined ? [] : [...mine.data.drafts, ...mine.data.submitted.items]
  )
    .filter((a) => a.sector?.id === panel.sector.id)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const shown = entries.slice(0, SHOWN);
  const columns: DataColumn<AwardSummary>[] = [
    {
      id: 'award',
      header: 'Award',
      cell: (a) => (
        <span className={s.heldText}>
          <Link href={`/awards/${a.id}`} className={s.strongLink}>
            {a.displayId ?? 'Draft'}
          </Link>
          {a.title !== null && <span className={s.meta}>{a.title}</span>}
        </span>
      ),
    },
    { id: 'updated', header: 'Updated', cell: (a) => formatShortDate(a.updatedAt), nowrap: true },
    {
      id: 'people',
      header: 'People',
      // D1 names who each entry is for (APP-070 D18); an older server's list keeps the count.
      cell: (a) => forWhomWords(a) ?? plural(a.people, 'person', 'people'),
    },
    { id: 'amount', header: 'Amount', cell: (a) => summaryAmount(a), numeric: true },
    {
      id: 'record',
      header: 'Record',
      cell: (a) => `by ${myName} for ${a.onBehalfOf?.name ?? lead}`,
    },
    {
      id: 'where',
      header: 'Where it is',
      cell: (a) => {
        const status = statusOf(a);
        return <StatusPill tone={status.tone} size="sm" label={status.label} />;
      },
    },
  ];
  return (
    <Card
      title="My entries"
      {...(headingRef && { headingRef })}
      help={{
        shows: `Awards you entered for ${lead} in ${panel.sector.name}, newest first, and where each one is now.`,
        canDo: 'Open an award’s record, or see every draft and entry in My drafts & submitted.',
      }}
      aside={<span className={s.meta}>{`Awards you entered for ${lead}`}</span>}
    >
      {mine.isError ? (
        <p className={s.cardText}>Your entries didn’t load. Reload the page to try again.</p>
      ) : (
        <DataTable
          caption={`My entries for ${lead}`}
          columns={columns}
          rows={shown}
          rowKey={(a) => a.id}
          minWidth={640}
          empty={
            mine.isPending
              ? { title: 'Loading your entries…', body: 'One moment.' }
              : {
                  // The title is a sentence of its own; the body follows it (D1.empty).
                  title: `Nothing entered for ${leadFirst} yet.`,
                  body: `Each award you start lands here once you save it, recorded “by ${myName} for ${lead}”, so you can follow it all the way to paid.`,
                }
          }
        />
      )}
      {entries.length > SHOWN && (
        <Link href="/awards" className={s.strongLink}>
          {`All ${String(entries.length)} entries in My drafts & submitted`}
        </Link>
      )}
    </Card>
  );
}
